import './MyStore.scss';

import {
  HomefieldIconChevronRight,
  HomefieldIconFulfillmentPickupOutlineNew,
} from '@dsg-tech/homefield-react-icons';
import { useCallback, useContext } from 'react';

import { WindowContext } from 'utility/windowContext';
import { handleADA } from '@dsg-tech/homefield-utils';
import { storeDetailsSelector } from 'store/selectors/locationServicesSelector';
import { useAppSelector } from 'store/store';
import useClientSideRender from 'hooks/useClientSideRender';
import {
  convert24HourTo12HourTime,
  createFormatedStoreHoursObject,
  getDayOfWeekWithOffset,
  getHoursFromMilitaryTime,
} from './StoreHoursUtilities';

interface MyStoreProps {
  viewPortBreakpoint: string;
  showInProgressiveTaxo?: boolean;
  setShowProgressiveTaxoDrawer?: () => void;
  showInMobileMenu?: boolean;
}

export const MyStoreFallback = () => (
  <div className="hmf-body-bold-l hmf-pl-xxs hmf-pr-xxxs">Find a Store</div>
);

const MyStore = ({
  viewPortBreakpoint,
  showInProgressiveTaxo = false,
  setShowProgressiveTaxoDrawer = () => {},
  showInMobileMenu = false,
}: MyStoreProps) => {
  const storeDetails = useAppSelector(storeDetailsSelector);
  const win = useContext(WindowContext);
  const { isClient } = useClientSideRender();

  const getNextTimeStoreOpen = useCallback(
    (storeHours: any) => {
      let nextDayAbbr = getDayOfWeekWithOffset(1);
      let nextDayStoreHours = storeHours[nextDayAbbr];

      if (nextDayStoreHours === '0000-0000') {
        nextDayAbbr = getDayOfWeekWithOffset(2);
        nextDayStoreHours = storeHours[nextDayAbbr];
      }

      nextDayStoreHours = createFormatedStoreHoursObject(nextDayStoreHours);
      nextDayAbbr = nextDayAbbr[0].toUpperCase() + nextDayAbbr.slice(1);

      return (
        <span className={showInProgressiveTaxo ? 'hmf-body-s' : 'hmf-legal-s'}>
          Opens <span className="hmf-text-transform-capitalize">{nextDayAbbr}</span> at{' '}
          {nextDayStoreHours.open}
        </span>
      );
    },
    [showInProgressiveTaxo],
  );

  const getStoreStatusMessage = useCallback(
    (storeHours: any) => {
      const now = new Date();
      const todayAbbr = getDayOfWeekWithOffset();
      const todaysStoreHours = createFormatedStoreHoursObject(storeHours[todayAbbr], true);

      const storeOpen = new Date();
      storeOpen.setHours(
        getHoursFromMilitaryTime(todaysStoreHours.open),
        Number(todaysStoreHours.open.slice(2)),
        0,
      );

      const storeClose = new Date();
      storeClose.setHours(
        getHoursFromMilitaryTime(todaysStoreHours.close),
        Number(todaysStoreHours.close.slice(2)),
        0,
      );

      if (now >= storeOpen && now <= storeClose) {
        return (
          <div className="store-hours hmf-body-s">
            {`Open Until ${convert24HourTo12HourTime(todaysStoreHours.close)}`}
          </div>
        );
      }

      if (now <= storeOpen) {
        return (
          <div className="store-hours hmf-body-s">
            {`Opens at ${convert24HourTo12HourTime(todaysStoreHours.open)}`}
          </div>
        );
      }

      if (now >= storeClose) {
        return <div className="store-hours hmf-body-s">{getNextTimeStoreOpen(storeHours)}</div>;
      }

      return <div className="store-hours hmf-body-s">Closed</div>;
    },
    [getNextTimeStoreOpen],
  );

  const handleShowMyStoreModal = () => {
    if (
      showInProgressiveTaxo &&
      setShowProgressiveTaxoDrawer &&
      (viewPortBreakpoint === 'desktop' || viewPortBreakpoint === 'small-desktop')
    ) {
      setShowProgressiveTaxoDrawer();
    }

    if (win && window?.headerFunctions?.storeSelection) {
      window.headerFunctions.storeSelection({});
    }
  };

  const getClassNames = () => {
    const classNames = 'my-store-button-new hmf-align-items-center hmf-px-xxs';

    if (showInProgressiveTaxo) {
      return `${classNames} store-locator-sdd-container hmf-py-xxs hmf-justify-content-between hmf-mr-xxs hmf-display-flex`;
    }

    if (showInMobileMenu) {
      return `${classNames} mobile-menu hmf-p-s hmf-px-md-m hmf-display-flex hmf-justify-content-between`;
    }

    return `${classNames} hmf-py-md-xxs hmf-display-none hmf-display-md-flex`;
  };

  return (
    <button
      data-testid="my-store-new"
      className={getClassNames()}
      type="button"
      tabIndex={0}
      aria-label={`The selected store is ${isClient ? storeDetails?.name : 'still loading'}`}
      onClick={handleShowMyStoreModal}
      onKeyDown={(e) => {
        handleADA(e, 'button', handleShowMyStoreModal);
      }}>
      <div className="hmf-display-flex hmf-text-align-left hmf-align-items-center">
        <HomefieldIconFulfillmentPickupOutlineNew width={24} height={24} />
        {isClient && storeDetails?.name ? (
          <div
            className={`hmf-text-align-left ${
              showInProgressiveTaxo ? 'hmf-pl-xxxs hmf-pl-xlg-xxs' : 'hmf-pl-xxs hmf-pr-xxxs'
            }`}>
            {!showInProgressiveTaxo && (
              <div className="hmf-body-bold-l hmf-text-transform-capitalize">
                {storeDetails.name.toLowerCase()}
              </div>
            )}
            {storeDetails.status == null || ['1', '2'].includes(storeDetails.status) ? (
              <div
                className={`store-hours store-closed ${
                  showInProgressiveTaxo ? 'hmf-body-s' : 'hmf-legal-s'
                }`}>
                Temporarily Closed
              </div>
            ) : (
              getStoreStatusMessage(storeDetails.storeHours)
            )}
            {showInProgressiveTaxo && (
              <div className="hmf-header-bold-xs store-name">{storeDetails.name.toLowerCase()}</div>
            )}
          </div>
        ) : (
          <MyStoreFallback />
        )}
      </div>
      {showInProgressiveTaxo && <HomefieldIconChevronRight height="16" width="16" />}
      {showInMobileMenu && <HomefieldIconChevronRight height="24" width="24" />}
    </button>
  );
};

export default MyStore;
